<template>
  <div class="page">
    <preview-bar v-if="isPreview" />
    <main v-if="pageData" class="page__content">
      <page-breadcrumbs
        v-if="pageData.databaseId"
        :breadcrumb-segments="breadcrumbSegments"
        :post-id="pageData.databaseId"
        :is-overlay="isPostPage"
        :is-inverted="isPostPage" />
      <blog-stage
        v-if="isPostPage"
        :blog-author="pageData.author?.node"
        :blog-date="pageData.dateGmt"
        :blog-excerpt="pageData.excerpt"
        :blog-categories="pageData.categories?.nodes"
        :blog-headline="pageData.title"
        :blog-image="pageData.featuredImage?.node" />

      <article class="page__article">
        <vue-enabled-html
          v-if="pageData.formattedContent"
          :content="pageData.formattedContent" />

        <div v-if="isPostPage" class="page__footer-share">
          <blocks-social-share />
        </div>
      </article>

      <div v-if="isPostPage" class="page__footer-related page__footer-related--products">
        <div class="page__footer-headline">
          What's new / What's trending?
        </div>
        <blocks-related-products
          header-text=""
          :product-limit="5"
          :slides-per-view="5"
          :post-id="pageData.databaseId" />
      </div>

      <div v-if="isPostPage" class="page__footer-related">
        <div class="page__footer-headline">
          Related blog articles
        </div>
        <blocks-related-posts :post-id="pageData.databaseId" />
      </div>
    </main>
    <not-found v-else-if="pageLoaded" />
  </div>
</template>

<script async setup lang="ts">
import {
  computed,
  defineAsyncComponent,
  navigateTo,
} from "#imports";

const NotFound = defineAsyncComponent(() => import('~/components/NotFound.vue'));

const props = defineProps<{
  pageData: Object,
  isPreview: Boolean,
}>();

const isPostPage = computed(() => {
  return props.pageData.contentTypeName === 'post';
});

const breadcrumbSegments = computed(() => {
  // if (pageData.value?.contentTypeName === 'post') {
  //   return [{
  //     name: 'Blog',
  //     url: '/blog'
  //   }]
  // }

  return []
});

const playTrack = (data) => {
  console.debug('Play audio id: ', data.detail.id);
}

const addToCart = async (productId) => {
  await this.cartStore.addToCart(productId,  1);
}

const goToRelative = (eventData) => {
  navigateTo(eventData.detail.relative_permalink);
}


</script>

<style lang="scss">
@use "sass:math";
@use "~/common/scss/mq";
@use "~/common/scss/variables";
@use "~/common/scss/container";
@use "~/common/scss/typography";
@use "~/common/scss/helpers";

.page {

  min-height: 95vh;

  &__content {
    position: relative;
  }

  &__article .content {
    @include container.m();

    @include mq.mobileTablet() {
      width: 95%;
    }

    display: block;
    font-size: helpers.px-to-rem(16);
    margin-bottom: helpers.px-to-rem(100);

    & > .well--paragraph-width,
    & > ul,
    p {
      margin-bottom: 1rem;
      color: #171717;
      font-weight: 300;

      & > iframe {
        aspect-ratio: 16 / 9;
        height: auto;
        width: 100%;
      }
    }

    ul {
      padding-left: 1.25rem;
    }

    & > .well {
      clear: both;
      margin: 0 auto 1rem;
      width: 100%;

      &--full-width {
        max-width: 100%;
      }

      .product-tiles {
        margin-bottom: 1.5rem;
      }

      p {
        max-width: 100%;
        width: 100%;

        a {
          color: #1e73be;
          text-decoration: none;
        }
      }
    }

    p iframe {
      aspect-ratio: 16 / 9;
      height: 100%;
      width: 100%;
    }

    img.aligncenter,
    & > .wp-embed-aspect-16-9 {
      display: block;
      margin: 2rem auto;
      width: 100%;

      iframe {
        aspect-ratio: 16 / 9;
        height: 100%;
        width: 100%;
      }
    }

    .wp-block-image,
    .wp-caption {
      margin: 2rem auto;

      img {
        max-height: 80vh;
        object-fit: contain;
        width: 100%;
      }

      figcaption {
        background-color: variables.$colorGreyED;
        border-radius: 3px;
        font-size: 1.1rem;
        font-weight: 300;
        margin-bottom: 1rem;
        padding: 0.8rem;
      }
    }

    hr {
      margin: 1.4rem 0;
    }

    figure {
      max-width: 100%;
    }

    blockquote {
      display: flex;
      gap: 3.5rem;
      margin: 0 auto;
      padding: 2rem 0;
      @include mq.mobileTablet() {
        flex-wrap: wrap;
      }

      &::before {
        color: variables.$colorGrey50;
        content: "“";
        flex: 0 0 helpers.px-to-rem(77);
        font-family: "Helvetica Neue", sans-serif;
        font-size: 180px;
        font-style: italic;
        height: 8rem;
        margin-top: -2.5rem;
        overflow: hidden;
        width: helpers.px-to-rem(77);
      }

      p {
        font-family: variables.$primaryFont;
        font-size: helpers.px-to-rem(30);
        font-style: italic;
        max-width: helpers.px-to-rem(860);
      }
    }

    .alignright {
      float: right;
      margin: 0 0 0 2rem;
    }

    .alignleft {
      float: left;
      margin: 0 2rem 0 0;
    }

    blockquote.pullleft {
      float: left;
      width: 50%;
      padding: 0;
    }

    blockquote.pullright {
      float: right;
      width: 50%;
      padding: 0;
    }
  }

  &__footer-share {
    @include container.paragraph();

    --share-size: 2rem;

    margin-bottom: helpers.px-to-rem(100);
    padding-left: 2px;

    width: 85%;
    max-width: var(--overlay-width, 70rem);

    .social-share__icons {
      gap: helpers.px-to-rem(2);
    }

    .social-share__icon {
      border: 1px solid black;
      border-radius: 50%;
    }
  }

  &__footer-related {
    @include container.xl();

    margin-bottom: helpers.px-to-rem(200);

    &--products {
      margin-bottom: helpers.px-to-rem(100);
    }

    .related-products {
      max-width: 100%;
      width: 100%;
    }
  }

  &__footer-headline {
    @include typography.h4();

    text-transform: uppercase;
  }

  iframe {
    max-width: 100%;
  }

  .jbutton {
    margin: 2rem 0;
    &--large {
      font-size: helpers.px-to-rem(18);
      padding: 1rem 2rem;
    }
  }

  .row {
    display: grid;
    gap: .5rem;
    grid-template-columns: repeat(12, 1fr);
    margin-bottom: 1rem;

    & > br {
      display: none; // I need to ignore those as they break css grid layout
    }

    .column {
      display: inline-block;
      grid-column: span 12;
      margin-left: 0;
      margin-right: 0;
      padding-left: 0;
      padding-right: 0;
      vertical-align: top;
      width: 100%;

      figure {
        width: 100% !important;
      }

      $legacyColumnCount: 2, 3, 4, 6, 8, 9;
      @each $columnCount in $legacyColumnCount {

        &.column-xs-#{$columnCount},
        &.column-md-#{$columnCount} {
          @include mq.aboveMobile() {
            grid-column: span $columnCount;
          }
        }
      }
    }
  }

  div.well a.product-tile__vendor-name {
    color: #171717;
    text-decoration: none;
  }

}

#form-682766 .wpcf7-form-control {
  height: 3rem;
}
</style>
